import React, {
  useState, useCallback, useEffect, useRef,
} from 'react'
import { createPortal } from 'react-dom'
import { useSelector } from 'react-redux'
import { useMutation } from '@apollo/client'
import { Map, fromJS } from 'immutable'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import { selectAuth } from '../../../../../services/auth/selectors'
import { selectUserLanguagePrimary } from '../../../../../services/user/index'
import { CREATE_PLAYLIST } from '../../../../../services/playlist/query'
import type {
  CommunityPlaylistCreatedDetails,
  CommunityPlaylistCreationMethod,
  CommunityPlaylistSmartCreateSource,
} from '../../../../PostHog/index'
import { H5 } from '../../../../Heading/index'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import Close from '../../../../Icon/icons/Close/index'
import ChevronLeft from '../../../../Icon/icons/ChevronLeft/index'
import PlaylistEmpty from '../../../../Icon/icons/PlaylistEmpty/index'
import SmartCreate from '../../../../Icon/icons/SmartCreate/index'
import Meditation from '../../../../Icon/icons/Meditation/index'
import Meditation2 from '../../../../Icon/icons/Meditation2/index'
import Star from '../../../../Icon/icons/Star/index'
import Check from '../../../../Icon/icons/Check/index'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from '../../../../Loading/index'
import SmartModalHero from '../../../shared/SmartModalHero/index'
import SparkleDivider from '../../../shared/SparkleDivider/index'
// Oracle UI primitives — reused on the new seed-prompt branch screen AND on
// the existing "Anything else?" page so both surfaces share the Oracle
// look (purple-outlined pills, oracle-chat-input container styling, the
// divination-feature-entry filled/outlined button variants).  See plan
// `we-want-a-drag-fancy-acorn.md` (Visual Refinement Round 2).
//
// No FloatingCard wrap — the modal frame already provides the white
// surface; an inner card was "smooshed" inside.  We render the seed-step
// content directly inside `.create-playlist-modal--smart` and reuse the
// Oracle classes directly:
//   - `.oracle-greeting-card__pill` for chips (purple-outlined pill)
//   - `.divination-feature-entry__button--outlined / --filled` for Back / Generate
//   - `.oracle-common-chat-input` from ChatInput, restyled in our SCSS to
//      mirror `.oracle-chat-input__container` (white bg + purple border)
import { ChatInput } from '../../../../OracleCommons/ChatInput'
import { OracleErrorToast } from '../../../../OracleCommons/OracleErrorToast'
// OracleErrorToast internally calls `useTranslatedText` against the
// `OracleCommons/StaticTextContext` — which THROWS without a provider in
// scope.  We wrap the toast in a minimal `<StaticTextProvider>` fed by
// `ORACLE_TOAST_FALLBACK_STATIC_TEXT` (defined below) so the hook's
// `['data', 'errors', 'generalError']` lookup resolves silently instead of
// warning on every render.  The visible message is always supplied via the
// `message` prop, so this fallback string is never displayed.
import {
  StaticTextProvider as OracleStaticTextProvider,
  type InternationalStrings,
} from '../../../../OracleCommons/StaticTextContext'
import type { StaticTextMap } from '../../../../../services/community/types'
import getText from '../../../../../services/community/shared/static-text-utils'
import { resolveNonCollidingPlaylistName } from '../../../../../services/community/shared/playlist-name-utils'
import useReservedSystemPlaylistNames from '../../../../../services/community/shared/useReservedSystemPlaylistNames'
import { usePlaylistGeneration, getDocumentThumbnail } from './usePlaylistGeneration'
import useSeedSuggestions from './useSeedSuggestions'
import useLoadingPhase from './useLoadingPhase'
import SmartFooter from './SmartFooter'
import TimeCustomCard from './TimeCustomCard'
import ResultsVideoGrid from './ResultsVideoGrid'

const EmptyPlaylistIcon = <PlaylistEmpty size={20} color="#FFFFFF" />
const SmartCreateIconEl = <SmartCreate size={20} color="#FFFFFF" />

type Step =
  | 'choose'
  | 'empty'
  /**
   * Seed-prompt branch screen — the user can either type / pick a prompt and
   * click Generate (quick path, skips mood/time/interests), or click
   * "Use mood + time" to continue into the existing wizard.  Renders with the
   * Oracle dark surface + ChatInput so it feels like the in-player Guide on Side.
   */
  | 'smart-seed'
  | 'smart-mood'
  | 'smart-time'
  | 'smart-interests'
  | 'generating'
  | 'results'

const TOTAL_SMART_STEPS = 3
const MAX_MOOD_PICKS = 2

interface MoodOption {
  /** Stable identifier — also the i18n lang key. */
  key: string
  /** Fallback English label rendered if the lang key is missing.
   *  Also forwarded into the alchemize prompt verbatim (e.g. "Calm"). */
  label: string
  /** Card icon rendered above the label. */
  icon: React.ReactNode
}

// NOTE(next): Four of these icons are placeholders (`Star`). Real SVGs for
// brain (Focused), stretching figure (Energized), alarm-clock-star (Inspired),
// and moon-stars (Restored) live in the Figma file at node-id=3687-54588 and
// should be exported into `lib/components/Icon/icons/Mood<Name>/` and swapped
// in here. Tracked in a follow-up ticket.
// Per-card pastel border colors are applied via `__mood-card--<key>`
// modifier classes in _CreatePlaylistModal.scss (Figma node-id=3687-54588).
const MOOD_OPTIONS: ReadonlyArray<MoodOption> = [
  { key: 'moodCalm', label: 'Calm', icon: <Meditation /> },
  { key: 'moodFocused', label: 'Focused', icon: <Star /> },
  { key: 'moodContent', label: 'Content', icon: <Meditation2 /> },
  { key: 'moodEnergized', label: 'Energized', icon: <Star /> },
  { key: 'moodInspired', label: 'Inspired', icon: <Star /> },
  { key: 'moodRestored', label: 'Restored', icon: <Star /> },
]

// targetDurationMinutes hint for the alchemize backend — midpoint of each
// time-bucket range. timeCustom uses the user-typed `customMinutes` directly.
const TIME_KEY_TO_MINUTES: Record<string, number> = {
  timeShort: 22,
  timeStandard: 37,
  timeMovie: 60,
}

const TIME_CUSTOM_KEY = 'timeCustom'

interface TimeOption {
  /** Stable identifier — sub-keyed as `${key}Primary` / `${key}Secondary` in lang files. */
  key: string
  primaryLabel: string
  /** Empty string for the Custom option — its secondary slot is a number input. */
  secondaryLabel: string
}

// Per-card pastel border colors are applied via `__time-card--<key>`
// modifier classes in _CreatePlaylistModal.scss (Figma node-id=3687-54589).
const TIME_OPTIONS: ReadonlyArray<TimeOption> = [
  { key: 'timeShort', primaryLabel: 'Short', secondaryLabel: '15–30 min' },
  { key: 'timeStandard', primaryLabel: 'Standard', secondaryLabel: '30–45 min' },
  { key: 'timeMovie', primaryLabel: 'Movie', secondaryLabel: '45+ min' },
  { key: TIME_CUSTOM_KEY, primaryLabel: 'Custom', secondaryLabel: '' },
]

// Minimal Immutable.Map shaped to satisfy `useTranslatedText` inside
// `OracleErrorToast`, which looks up `['data', 'errors', 'generalError']`
// on every render. Passing `undefined` to `OracleStaticTextProvider` made
// the hook log a console warning on every render of the modal. The visible
// toast message is always supplied via the `message` prop, so this string
// is only ever read by the hook's fallback path — it never reaches the UI.
// Kept module-scoped so the same Map instance is reused across renders.
const ORACLE_TOAST_FALLBACK_STATIC_TEXT = fromJS({
  data: {
    errors: {
      generalError: 'Something went wrong. Please try again.',
    },
  },
}) as unknown as InternationalStrings

interface CreatePlaylistModalProps {
  onClose: () => void
  /**
   * Fires after a successful playlist create. The `method` discriminant tells
   * the parent which creation path was used so it can dispatch the matching
   * PostHog `community_playlist_created` event with the right `method`
   * property. `details` carries the smart-create structured signals (items
   * count, prompt source, etc.) when `method === 'smart_create'`, and a
   * minimal `{ was_auto_renamed }` for the empty path. The parent forwards
   * `details` straight to the PostHog callback. Both types come from
   * `components/PostHog` so the modal and the tracker stay in lockstep.
   */
  onCreated?: (
    method: CommunityPlaylistCreationMethod,
    details?: CommunityPlaylistCreatedDetails,
  ) => void
  staticText: StaticTextMap
  /**
   * Names of the user's existing playlists. Drives the auto-suffix walk
   * for both the empty-create and Smart Create save paths so a typed
   * name that collides with a reserved system playlist OR an existing
   * custom playlist gets silently saved as "<name> 2", "<name> 3", etc.
   */
  existingPlaylistNames?: ReadonlyArray<string>
  /**
   * Fired after a successful create when the saved name was auto-rewritten
   * from the user's typed text. The parent should surface a non-blocking
   * toast so the user understands why the saved name differs.
   */
  onAutoRenamed?: (originalName: string, finalName: string) => void
}

interface CreatePlaylistResponse {
  playlistCreate: {
    playlist: {
      id: string
      uid: string
      playlistType: string
      playlistName: string
    }
  }
}

function CreatePlaylistModal ({
  onClose,
  onCreated,
  staticText,
  existingPlaylistNames,
  onAutoRenamed,
}: CreatePlaylistModalProps) {
  const auth = useSelector(selectAuth)
  const jwt = auth.get('jwt')
  const language = useSelector(
    (state: { user: Map<string, unknown> }) => selectUserLanguagePrimary(state),
  ) || 'en'

  const [step, setStep] = useState<Step>('choose')

  // Empty form state
  const [playlistName, setPlaylistName] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Smart create state (multi-page Figma redesign)
  const [selectedMoods, setSelectedMoods] = useState<string[]>([]) // mood.key, max 2
  const [selectedTime, setSelectedTime] = useState<string | null>(null) // time.key, single
  // Only used when selectedTime === TIME_CUSTOM_KEY
  const [customMinutes, setCustomMinutes] = useState<number | null>(null)
  const [interestsText, setInterestsText] = useState('') // free-text page 3
  // Seed-prompt branch screen state.  The user can either type here and hit
  // Generate (quick path) or click "Use mood + time" to fall through to the
  // existing 3-page wizard (in which case `seedPromptText` is a discarded
  // scratchpad; the mood path's buildAlchemizePrompt() ignores it).
  const [seedPromptText, setSeedPromptText] = useState('')
  // Results state
  const [playlistNameForSave, setPlaylistNameForSave] = useState('')
  const [createPlaylistMutation] = useMutation<CreatePlaylistResponse>(CREATE_PLAYLIST, {
    context: { jwt },
  })

  const reservedNames = useReservedSystemPlaylistNames(staticText)

  // Track the previous non-generating step so we can ghost its content
  // during generating, keeping the modal at the same size.
  const previousStep = useRef<Step>('choose')
  const emptyInputRef = useRef<HTMLInputElement>(null)
  // Cache the exact (prompt, options) used by the most recent generate call
  // so the results-step "Try Again" button can replay it. The two generate
  // paths (seed quick-path and the mood/time/interests wizard) build prompts
  // from different state — rebuilding from current state on reroll silently
  // drops the prompt whenever the user came in via the other path.
  const lastGenerationRef = useRef<{
    prompt: string
    options: { targetDurationMinutes?: number; maxVideos: number }
  } | null>(null)

  // Seed-suggestion pills for the smart-seed branch screen. Skipped
  // until the user enters smart-seed; `network-only` then fires a fresh
  // fetch on every entry so the chip set rotates per visit (choose ↔
  // smart-seed bouncing, modal close+reopen, etc.). No client-side
  // fallback list — on loading / error / empty response the chip row
  // simply renders empty and the user types a free-form prompt.
  const {
    isGenerating,
    isSaving: isSavingPlaylist,
    documents,
    narrativeText,
    suggestedName,
    error: generationError,
    generate,
    reroll,
    save: savePlaylist,
    reset: resetGeneration,
  } = usePlaylistGeneration(jwt, language)

  // 3-phase loading copy on the generating step.
  const loadingPhase = useLoadingPhase(step)

  // Server-curated seed list. Backend guarantees one seed per listed
  // distributed category + fills remaining slots from the full pool —
  // render in response order, no client-side re-sorting.
  const seedSuggestions = useSeedSuggestions(jwt, step)

  // Update previousStep whenever step changes (but not to 'generating' itself)
  useEffect(() => {
    if (step !== 'generating') {
      previousStep.current = step
    }
  }, [step])

  // Transition from generating -> results when generation completes.
  // Prefer the backend's suggestedName (LLM-curated 3-8 word name); fall
  // back to the generic default copy only if the backend returned no
  // suggestion.
  //
  // Depend on `hasResults` (primitive boolean) rather than `documents`
  // (array reference) so the effect only fires on the empty→non-empty
  // transition, not on every setDocuments call (which can land during
  // reset or from a stale-response no-op). See plan
  // `do-a-code-review-eager-twilight.md` (finding #6).
  const hasResults = documents.length > 0
  useEffect(() => {
    if (step === 'generating' && !isGenerating && hasResults) {
      const fallbackName = getText(staticText, 'autoNameDefault', 'My Smart Playlist')
      setPlaylistNameForSave(suggestedName || fallbackName)
      setStep('results')
    }
  }, [step, isGenerating, hasResults, staticText, suggestedName])

  // Show generation errors on the generating step
  useEffect(() => {
    if (step === 'generating' && generationError && !isGenerating) {
      setError(generationError)
    }
  }, [step, generationError, isGenerating])

  // Focus the playlist name input when entering the empty step
  useEffect(() => {
    if (step === 'empty') {
      emptyInputRef.current?.focus()
    }
  }, [step])

  // ── Empty playlist handlers ──

  const handleEmptySubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = playlistName.trim()
    if (!trimmed) return

    const finalName = resolveNonCollidingPlaylistName(
      trimmed,
      reservedNames,
      existingPlaylistNames || [],
    )

    setSaving(true)
    setError(null)
    try {
      await createPlaylistMutation({ variables: { playlistName: finalName } })
      if (finalName !== trimmed && onAutoRenamed) onAutoRenamed(trimmed, finalName)
      if (onCreated) {
        onCreated('empty', {
          was_auto_renamed: finalName !== trimmed,
        })
      }
      onClose()
    } catch {
      setError(getText(staticText, 'errorCreate', 'Unable to create playlist. Please try again.'))
      setSaving(false)
    }
  }, [
    playlistName, createPlaylistMutation, onCreated, onClose, staticText,
    reservedNames, existingPlaylistNames, onAutoRenamed,
  ])

  // ── Smart create handlers ──

  const toggleMood = useCallback((moodKey: string) => {
    setSelectedMoods((prev) => {
      if (prev.includes(moodKey)) return prev.filter((m) => m !== moodKey)
      if (prev.length >= MAX_MOOD_PICKS) return prev
      return [...prev, moodKey]
    })
  }, [])

  const handleSmartNext = useCallback(() => {
    if (step === 'smart-mood' && selectedMoods.length > 0) {
      setStep('smart-time')
    } else if (step === 'smart-time' && selectedTime !== null) {
      setStep('smart-interests')
    }
  }, [step, selectedMoods.length, selectedTime])

  const handleSmartBack = useCallback(() => {
    if (step === 'smart-mood') {
      // Mood is no longer the first smart step — Back returns to the seed
      // branch screen so the user can re-enter the quick prompt path.
      setStep('smart-seed')
    } else if (step === 'smart-time') {
      setStep('smart-mood')
    } else if (step === 'smart-interests') {
      setStep('smart-time')
    }
  }, [step])

  // ── Seed-prompt branch handlers ──

  /**
   * Quick path: user typed (or picked) a seed prompt on `smart-seed` and hit
   * Generate.  The alchemize call uses just the typed text — no `Mood:` /
   * `Length:` prefix — so the backend can interpret it as a free-form ask.
   * Returns to nothing on empty input; the button is also disabled in that case.
   *
   * Accepts an optional `rawText` argument so the ChatInput's send-button
   * (`<button class="oracle-icon-button oracle-common-chat-input__button">`)
   * and the inline Generate button (`<button class="…__seed-generate-btn">`)
   * fire through the exact same code path.  ChatInput passes the live
   * internal text via `onSendMessage(text)`; the Generate button calls
   * with no arg, in which case we read `seedPromptText` from state (safe
   * because the button is disabled while the trimmed value is empty).
   */
  const handleSeedGenerate = useCallback((rawText?: string) => {
    const source = rawText ?? seedPromptText
    const prompt = source.trim()
    if (!prompt) return
    setSeedPromptText(prompt)
    setError(null)
    setStep('generating')
    // No targetDurationMinutes for the quick path — let the backend infer
    // a sensible length from the prompt itself.
    const options = { maxVideos: 10 }
    lastGenerationRef.current = { prompt, options }
    generate(prompt, options)
  }, [seedPromptText, generate])

  /** Back from the seed branch returns to the choose step. */
  const handleSeedBackToChoose = useCallback(() => {
    setStep('choose')
  }, [])

  /**
   * Resolve the time phrase for the prompt. Custom uses the user-typed
   * minute count; the named buckets render `"<Primary> (<Secondary>)"`.
   */
  const resolveTimePhrase = useCallback((): string => {
    if (selectedTime === TIME_CUSTOM_KEY) {
      return customMinutes && customMinutes > 0 ? `${customMinutes} min` : ''
    }
    const timeOption = TIME_OPTIONS.find((t) => t.key === selectedTime)
    return timeOption ? `${timeOption.primaryLabel} (${timeOption.secondaryLabel})` : ''
  }, [selectedTime, customMinutes])

  /**
   * Compute the targetDurationMinutes hint passed to alchemize.
   * Returns `customMinutes` for Custom, the bucket midpoint otherwise.
   */
  const resolveTargetMinutes = useCallback((): number | undefined => {
    if (!selectedTime) return undefined
    if (selectedTime === TIME_CUSTOM_KEY) {
      return customMinutes && customMinutes > 0 ? customMinutes : undefined
    }
    return TIME_KEY_TO_MINUTES[selectedTime]
  }, [selectedTime, customMinutes])

  /**
   * Serialize the wizard's structured choices into an alchemize prompt.
   * The backend LLM interprets natural-language input; we feed it a
   * pipe-free "Mood: X. Length: Y. <interests>" sentence.
   */
  const buildAlchemizePrompt = useCallback((): string => {
    const moodLabels = selectedMoods.map(
      (k) => MOOD_OPTIONS.find((m) => m.key === k)?.label ?? k,
    )
    const timePhrase = resolveTimePhrase()
    const parts: string[] = []
    if (moodLabels.length) parts.push(`Mood: ${moodLabels.join(', ')}`)
    if (timePhrase) parts.push(`Length: ${timePhrase}`)
    const interestsTrimmed = interestsText.trim()
    if (interestsTrimmed) parts.push(interestsTrimmed)
    return parts.join('. ')
  }, [selectedMoods, resolveTimePhrase, interestsText])

  const handleSmartGenerate = useCallback(() => {
    setError(null)
    setStep('generating')
    const prompt = buildAlchemizePrompt()
    const options = { targetDurationMinutes: resolveTargetMinutes(), maxVideos: 10 }
    lastGenerationRef.current = { prompt, options }
    generate(prompt, options)
  }, [buildAlchemizePrompt, generate, resolveTargetMinutes])

  const handleReroll = useCallback(() => {
    // Replay the exact (prompt, options) used by the most recent generate.
    // The seed quick-path and the mood/time/interests wizard build prompts
    // from disjoint state, so rebuilding here would silently drop the
    // prompt for whichever path the user did NOT take.
    const last = lastGenerationRef.current
    if (!last) return
    setError(null)
    setStep('generating')
    reroll(last.prompt, last.options)
  }, [reroll])

  const handleChangePrefs = useCallback(() => {
    resetGeneration()
    setError(null)
    setStep('smart-mood')
  }, [resetGeneration])

  const handleSavePlaylist = useCallback(async () => {
    const name = playlistNameForSave.trim()
    if (!name || documents.length === 0) return

    const finalName = resolveNonCollidingPlaylistName(
      name,
      reservedNames,
      existingPlaylistNames || [],
    )

    // Map alchemize documents → items shape accepted by the atomic
    // `playlistCreateWithItems` mutation. The new endpoint takes contentType
    // per item (PlaylistDocument always carries it; default to 'video' as
    // a last-resort safety net) and preserves array order as playlist
    // position — no inversion needed.
    const items = documents.map((doc) => ({
      contentId: doc.contentId,
      contentType: doc.contentType || doc.content?.contentType || 'video',
    }))
    // Use the backend's curated 1-2 sentence narrative as the playlist
    // description. Empty narrative → empty description; the save mutation
    // already coerces falsy strings to null at the GraphQL boundary.
    const description = narrativeText.trim()

    const result = await savePlaylist(finalName, description, items)
    if (result?.success) {
      if (finalName !== name && onAutoRenamed) onAutoRenamed(name, finalName)
      if (onCreated) {
        // Resolve which seed input path the user took. The mood/time/interests
        // wizard branches are currently unreachable from the UI but already
        // plumbed via `selectedMoods`; we treat any mood selection as a wizard
        // run. Otherwise, an exact-text match against the server-curated pill
        // set identifies a `'suggestion'` save; everything else is `'typed'`
        // free-form input through the ChatInput.
        const lastPrompt = lastGenerationRef.current?.prompt ?? ''
        const isFromSuggestion = lastPrompt.length > 0
          && seedSuggestions.includes(lastPrompt)
        let promptSource: CommunityPlaylistSmartCreateSource = 'typed'
        if (selectedMoods.length > 0) {
          promptSource = 'wizard'
        } else if (isFromSuggestion) {
          promptSource = 'suggestion'
        }

        onCreated('smart_create', {
          items_count: documents.length,
          target_duration_minutes:
            lastGenerationRef.current?.options.targetDurationMinutes ?? null,
          has_user_edited_name: suggestedName != null && name !== suggestedName,
          narrative_present: narrativeText.trim().length > 0,
          prompt_source: promptSource,
          was_auto_renamed: finalName !== name,
        })
      }
      onClose()
    }
  }, [
    playlistNameForSave, documents, narrativeText,
    savePlaylist, onCreated, onClose,
    reservedNames, existingPlaylistNames, onAutoRenamed,
    suggestedName, selectedMoods, seedSuggestions,
  ])

  // `isSmartStep` controls the minimal header (close-only) AND the
  // `--smart` width modifier.  smart-seed reuses both so it gets the same
  // 628 px desktop max-width and close-only header.
  const isSmartStep = step === 'smart-seed'
    || step === 'smart-mood'
    || step === 'smart-time'
    || step === 'smart-interests'
  let smartStepIndex = 1
  if (step === 'smart-time') smartStepIndex = 2
  else if (step === 'smart-interests') smartStepIndex = 3

  // ── Renderers ──

  const renderHeader = () => {
    // Smart sub-steps don't render the legacy title row — the SmartModalHero
    // (eyebrow + question) handles the heading inside the step body. We
    // still need a top-right close button on smart steps, so render a
    // minimal header.
    //
    // EXCEPTION: smart-seed AND the results step promote the SmartModalHero
    // eyebrow into this header row so the eyebrow ("✨ CREATE A PLAYLIST")
    // sits inline with the close button — eyebrow on the left, × on the
    // right.  The in-step SmartModalHero gets `eyebrow=""` (handled in
    // renderSmartSeedStep / renderResultsStep) so the eyebrow renders
    // exactly once.  Mood / time / interests keep the close-only minimal
    // header — their in-step SmartModalHero still owns the eyebrow.
    const usesSeedHeader = step === 'smart-seed' || step === 'results'
    if (isSmartStep || step === 'results') {
      const headerClass = [
        'create-playlist-modal__header',
        'create-playlist-modal__header--smart',
        usesSeedHeader ? 'create-playlist-modal__header--smart-seed' : '',
      ].filter(Boolean).join(' ')
      return (
        <div className={headerClass}>
          {usesSeedHeader ? (
            <span className="smart-modal-hero__eyebrow create-playlist-modal__header-eyebrow">
              <span className="smart-modal-hero__eyebrow-icon" aria-hidden="true">
                <SmartCreate size={16} />
              </span>
              <span className="smart-modal-hero__eyebrow-text">
                {step === 'results'
                  ? getText(staticText, 'alchemizedHeroEyebrow', 'Alchemized Playlist')
                  : getText(staticText, 'smartHeroEyebrow', 'Alchemize a Playlist')}
              </span>
            </span>
          ) : null}
          <Button
            type={BUTTON_TYPES.GHOST}
            className="create-playlist-modal__close"
            onClick={onClose}
            aria-label={getText(staticText, 'closeAriaLabel', 'Close')}
          >
            <Close />
          </Button>
        </div>
      )
    }

    // choose / empty / generating-when-not-from-results — legacy title row.
    let title = getText(staticText, 'titleCreate', 'Create a playlist')
    if (step === 'generating') title = getText(staticText, 'titleGenerating', 'Generating playlist…')

    const HeadingTag = H5

    return (
      <div className="create-playlist-modal__header">
        <HeadingTag className="create-playlist-modal__title">{title}</HeadingTag>
        <Button
          type={BUTTON_TYPES.GHOST}
          className="create-playlist-modal__close"
          onClick={onClose}
          aria-label={getText(staticText, 'closeAriaLabel', 'Close')}
        >
          <Close />
        </Button>
      </div>
    )
  }

  const renderChooseStep = () => (
    <div className="create-playlist-modal__choices">
      <div className="create-playlist-modal__choice-card">
        <Button
          type={BUTTON_TYPES.PRIMARY}
          className="create-playlist-modal__choice-button create-playlist-modal__choice-button--empty"
          onClick={() => setStep('empty')}
        >
          {EmptyPlaylistIcon}
          <span className="create-playlist-modal__choice-title">
            {getText(staticText, 'startEmptyTitle', 'Start empty')}
          </span>
        </Button>
        <span className="create-playlist-modal__choice-desc">
          {getText(staticText, 'startEmptyDesc', 'Create a blank playlist and add episodes later at your own pace.')}
        </span>
      </div>
      <div className="create-playlist-modal__choice-card">
        <Button
          type={BUTTON_TYPES.PRIMARY}
          className="create-playlist-modal__choice-button create-playlist-modal__choice-button--smart"
          onClick={() => setStep('smart-seed')}
        >
          {SmartCreateIconEl}
          <span className="create-playlist-modal__choice-title">
            {getText(staticText, 'alchemizeTitle', 'Alchemize')}
          </span>
        </Button>
        <span className="create-playlist-modal__choice-desc">
          {getText(staticText, 'alchemizeDesc', 'Describe what you want and we\'ll alchemize a playlist tailored to your vibe.')}
        </span>
      </div>
    </div>
  )

  const renderEmptyStep = () => (
    <form className="create-playlist-modal__empty-form" onSubmit={handleEmptySubmit}>
      <input
        type="text"
        className="create-playlist-modal__input"
        placeholder={getText(staticText, 'playlistNamePlaceholder', 'Playlist name')}
        value={playlistName}
        onChange={(e) => setPlaylistName(e.target.value)}
        maxLength={100}
        ref={emptyInputRef}
        aria-label={getText(staticText, 'playlistNameAriaLabel', 'Playlist name')}
      />
      {error ? (
        <span className="create-playlist-modal__error">{error}</span>
      ) : null}
      <div className="create-playlist-modal__actions">
        <Button type={BUTTON_TYPES.GHOST} className="create-playlist-modal__back-btn" onClick={() => setStep('choose')}>
          {getText(staticText, 'back', 'Back')}
        </Button>
        <Button
          type={BUTTON_TYPES.PRIMARY}
          className="create-playlist-modal__create-btn"
          onClick={handleEmptySubmit}
          disabled={!playlistName.trim() || saving}
        >
          {saving
            ? getText(staticText, 'creating', 'Creating…')
            : getText(staticText, 'create', 'Create')}
        </Button>
      </div>
    </form>
  )

  // ── Smart sub-step renderers ──

  const renderSmartFooter = (
    primaryLabel: string,
    onPrimaryClick: () => void,
    isPrimaryDisabled: boolean,
    isFirstSmartStep: boolean,
  ) => (
    <SmartFooter
      backLabel={getText(staticText, 'back', 'Back')}
      primaryLabel={primaryLabel}
      onBack={handleSmartBack}
      onPrimary={onPrimaryClick}
      isPrimaryDisabled={isPrimaryDisabled}
      isFirstSmartStep={isFirstSmartStep}
      totalDots={TOTAL_SMART_STEPS}
      activeDot={smartStepIndex}
    />
  )

  const renderSmartMoodStep = () => (
    <div className="create-playlist-modal__smart">
      <SmartModalHero
        eyebrow={getText(staticText, 'smartHeroEyebrow', 'Alchemize a Playlist')}
        question={getText(staticText, 'smartMoodQuestion', 'What is your desired mood?')}
        hint={getText(staticText, 'smartMoodHint', 'Pick up to 2')}
      />
      <SparkleDivider />
      <div className="create-playlist-modal__mood-grid">
        {MOOD_OPTIONS.map((option) => {
          const isSelected = selectedMoods.includes(option.key)
          const isDisabled = !isSelected && selectedMoods.length >= MAX_MOOD_PICKS
          const cardClass = [
            'create-playlist-modal__mood-card',
            // Per-key border-color modifier (Figma 3687-54588).
            `create-playlist-modal__mood-card--${option.key}`,
            isSelected ? 'create-playlist-modal__mood-card--selected' : '',
            isDisabled ? 'create-playlist-modal__mood-card--disabled' : '',
          ].filter(Boolean).join(' ')
          return (
            <Button
              key={option.key}
              type={BUTTON_TYPES.GHOST}
              className={cardClass}
              onClick={() => toggleMood(option.key)}
              disabled={isDisabled}
            >
              <span className="create-playlist-modal__mood-icon" aria-hidden="true">
                {option.icon}
              </span>
              <span className="create-playlist-modal__mood-label">
                {getText(staticText, option.key, option.label)}
                {isSelected ? (
                  <Check
                    size="16px"
                    color="#137F89"
                    className="create-playlist-modal__mood-check"
                  />
                ) : null}
              </span>
            </Button>
          )
        })}
      </div>
      {renderSmartFooter(
        getText(staticText, 'next', 'Next'),
        handleSmartNext,
        selectedMoods.length === 0,
        // smart-mood is no longer the first smart step — the seed-prompt
        // branch screen precedes it, so render the Back button (which
        // returns to smart-seed via handleSmartBack).
        false,
      )}
    </div>
  )

  const renderSmartTimeStep = () => {
    // Time step's Next is disabled until a card is selected. For the
    // Custom card we additionally require a valid positive integer in
    // `customMinutes`.
    const isCustomSelected = selectedTime === TIME_CUSTOM_KEY
    const isCustomValid = !!customMinutes && customMinutes > 0
    const isNextDisabled = selectedTime === null || (isCustomSelected && !isCustomValid)
    return (
      <div className="create-playlist-modal__smart">
        <SmartModalHero
          eyebrow={getText(staticText, 'smartHeroEyebrow', 'Alchemize a Playlist')}
          question={getText(staticText, 'smartTimeQuestion', 'How much time do you have?')}
          hint={getText(staticText, 'smartTimeHint', 'Pick 1')}
        />
        <SparkleDivider />
        <div className="create-playlist-modal__time-grid">
          {TIME_OPTIONS.map((option) => {
            const isSelected = selectedTime === option.key
            const cardClass = [
              'create-playlist-modal__time-card',
              // Per-key border-color modifier (Figma 3687-54589).
              `create-playlist-modal__time-card--${option.key}`,
              isSelected ? 'create-playlist-modal__time-card--selected' : '',
              option.key === TIME_CUSTOM_KEY ? 'create-playlist-modal__time-card--custom' : '',
            ].filter(Boolean).join(' ')
            const primary = (
              <span className="create-playlist-modal__time-primary">
                {getText(
                  staticText,
                  `${option.key}Primary`,
                  option.primaryLabel,
                )}
                {isSelected ? (
                  <Check
                    size="16px"
                    color="#137F89"
                    className="create-playlist-modal__time-check"
                  />
                ) : null}
              </span>
            )
            // Custom card: TimeCustomCard manages its own input ref + focus +
            // keyboard. Typing auto-selects this card via onChange.
            if (option.key === TIME_CUSTOM_KEY) {
              const primaryLabel = getText(
                staticText,
                `${option.key}Primary`,
                option.primaryLabel,
              )
              return (
                <TimeCustomCard
                  key={option.key}
                  isSelected={isSelected}
                  customMinutes={customMinutes}
                  onSelect={() => setSelectedTime(TIME_CUSTOM_KEY)}
                  onChange={(value) => {
                    setCustomMinutes(value)
                    if (selectedTime !== TIME_CUSTOM_KEY) setSelectedTime(TIME_CUSTOM_KEY)
                  }}
                  placeholder={getText(staticText, 'customMinutesPlaceholder', 'Minutes')}
                  ariaLabel={getText(
                    staticText,
                    'customMinutesAriaLabel',
                    'Custom playlist duration in minutes',
                  )}
                  primaryLabel={primaryLabel}
                  unitLabel="min"
                  cardClassName={cardClass}
                />
              )
            }
            return (
              <Button
                key={option.key}
                type={BUTTON_TYPES.GHOST}
                className={cardClass}
                onClick={() => setSelectedTime(option.key)}
              >
                {primary}
                <span className="create-playlist-modal__time-secondary">
                  {getText(staticText, `${option.key}Secondary`, option.secondaryLabel)}
                </span>
              </Button>
            )
          })}
        </div>
        {renderSmartFooter(
          getText(staticText, 'next', 'Next'),
          handleSmartNext,
          isNextDisabled,
          false,
        )}
      </div>
    )
  }

  // ── Seed-prompt branch screen (smart-seed) ─────────────────────────────
  // Wrapped in <FloatingCard className="oracle-greeting-card …"> so the
  // surface (white bg + thin purple border + soft shadow + responsive
  // padding) and child pill / heading styles match the Oracle Greeting
  // Card on `/oracle`.  No `OracleThemeProvider` wrap — the light-theme
  // CSS variables (`--oracle-bg-primary`, `--oracle-accent`, etc.) are
  // defined at `:root` in `oracle-theme-variables.scss`, so FloatingCard
  // resolves them globally without an ancestor provider.
  //
  // Visible actions are reduced to **Back** + **Generate**.  The
  // "Or pick mood + time" opt-out is intentionally commented out for a
  // future release — the mood / time / interests wizard renderers stay in
  // this file but are unreachable from the UI in this release.  Restoring
  // is a one-line uncomment of the secondary `<Button>` block.
  //
  // No CarouselDots — this screen is a branch entry, not part of the
  // 3-page wizard count.
  const renderSmartSeedStep = () => (
    <div className="create-playlist-modal__smart create-playlist-modal__smart-seed">
      {/* Empty `eyebrow=""` — the eyebrow is rendered in renderHeader for
          smart-seed (inline with the close button) so it doesn't appear
          twice.  SmartModalHero gates the eyebrow span on truthy. */}
      <SmartModalHero
        eyebrow=""
        question={getText(staticText, 'smartSeedQuestion', 'What would you like a playlist about?')}
        hint={getText(staticText, 'smartSeedHint', 'Type your idea or tap a suggestion')}
      />
      <SparkleDivider />
      {/* Seed-suggestion chips — bare `<button>` elements styled via
          `.oracle-greeting-card__pill` (the rule that powers the Oracle
          Greeting Card pill row on `/oracle`).  We deliberately do NOT
          add `create-playlist-modal__seed-chip` here — that local class
          forced a teal hover that fought the Oracle pill's hover lift.
          The `--branch` row modifier centers the chips for the branch
          screen layout. */}
      <div
        className={[
          'oracle-greeting-card__pill-row',
          'create-playlist-modal__seed-chips',
          'create-playlist-modal__seed-chips--branch',
        ].join(' ')}
      >
        {seedSuggestions.map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            className="oracle-greeting-card__pill"
            onClick={() => setSeedPromptText(suggestion)}
            aria-label={`${getText(staticText, 'useSeedAriaLabel', 'Use seed:')} ${suggestion}`}
          >
            {suggestion}
          </button>
        ))}
      </div>
      {/* ChatInput renders `<textarea id="oracle-common-chat-input-textarea"
          className="oracle-common-chat-input__textarea">` and an
          `oracle-icon-button oracle-common-chat-input__button` send-button
          internally.  Our SCSS override on `.create-playlist-modal__seed-input
          .oracle-common-chat-input` re-skins the container to match
          `.oracle-chat-input__container` (white bg + purple-accent border)
          since the default styling is scrim-medium dark. */}
      <div className="create-playlist-modal__seed-input">
        <ChatInput
          value={seedPromptText}
          onValueChange={setSeedPromptText}
          // ChatInput's send-button (and Enter) fire through the SAME
          // `handleSeedGenerate` callback the Generate button uses.  The
          // function accepts an optional rawText so the live ChatInput
          // value (immune to state-update lag) flows through, while the
          // Generate-button no-arg path reads `seedPromptText` from
          // state — both end up calling `generate(prompt, { maxVideos: 10 })`.
          onSendMessage={handleSeedGenerate}
          placeholder={getText(
            staticText,
            'smartSeedPlaceholder',
            'Ask for a playlist…',
          )}
          autoFocus
          disabled={isGenerating}
        />
      </div>
      {/* Action row reduced to Back + Generate.  Buttons reuse the
          `.divination-feature-entry__button` variants from
          `lib/components/OraclePage/DivinationFeatureEntry/_DivinationFeatureEntry.scss`
          — `--outlined` for Back (transparent + purple border) and
          `--filled` for Generate (solid purple).  Bare `<button>` elements
          mirror the DivinationFeatureEntry component itself, which uses
          the same pattern.  No `<Button>` component wrap — that would
          double-paint with `button button--ghost` / `button--primary`
          classes that compete with the divination variants. */}
      <div className="create-playlist-modal__seed-actions">
        <button
          type="button"
          className={[
            'divination-feature-entry__button',
            'divination-feature-entry__button--outlined',
            'create-playlist-modal__seed-back-btn',
          ].join(' ')}
          onClick={handleSeedBackToChoose}
        >
          <ChevronLeft />
          {getText(staticText, 'back', 'Back')}
        </button>
        {/*
          // FUTURE-RELEASE — re-enable when the mood + time wizard returns.
          // Wired to handleSeedUseMoodFlow which sets step to 'smart-mood'.
          // The wizard renderers (renderSmartMoodStep / renderSmartTimeStep /
          // renderSmartInterestsStep) remain in the component and pick up
          // where this would route.
          <button
            type="button"
            className={[
              'divination-feature-entry__button',
              'divination-feature-entry__button--outlined',
              'create-playlist-modal__seed-mood-btn',
            ].join(' ')}
            onClick={handleSeedUseMoodFlow}
          >
            {getText(staticText, 'useMoodFlow', 'Or pick mood + time')}
          </button>
        */}
        <button
          type="button"
          className={[
            'divination-feature-entry__button',
            'divination-feature-entry__button--filled',
            'create-playlist-modal__seed-generate-btn',
          ].join(' ')}
          // No-arg call — handleSeedGenerate falls back to seedPromptText
          // from state.  Wrapping in `() => …` so the MouseEvent that
          // React would otherwise pass as the first arg doesn't get
          // interpreted as a rawText override.
          onClick={() => handleSeedGenerate()}
          disabled={!seedPromptText.trim() || isGenerating}
        >
          {getText(staticText, 'generate', 'Generate')}
        </button>
      </div>
      {/* Oracle-styled error toast.  Wrapped in `OracleStaticTextProvider`
          because the toast's internal `useTranslatedText` hook throws
          without a provider in scope.  We pass `message` explicitly so the
          fallback (just the last key string) is never displayed.  The
          toast auto-dismisses after 4 s; `onClose` clears our local error
          state so it can re-trigger on the next failure. */}
      <OracleStaticTextProvider staticText={ORACLE_TOAST_FALLBACK_STATIC_TEXT}>
        <OracleErrorToast
          isVisible={!!error}
          onClose={() => setError(null)}
          message={error || undefined}
        />
      </OracleStaticTextProvider>
    </div>
  )

  // ── "Anything else?" step (smart-interests) ─────────────────────────────
  // Refreshed to share the seed-screen's Oracle Greeting Card surface
  // (`FloatingCard` + `.oracle-greeting-card`).  ChatInput replaces the
  // prior `<textarea>` + voice button; bare `<button class="…__pill">`
  // chips replace the OraclePillButton ones.  Enter inside the ChatInput
  // submits the Create flow via `handleSmartGenerate` — matching what the
  // wizard footer's primary button already does — so the wizard's
  // CarouselDots + footer remain in control of the UI affordance.
  //
  // Note: this page is currently unreachable from the seed-prompt screen
  // because the "Or pick mood + time" opt-out is commented out for a
  // future release.  The renderer stays in the file for the inevitable
  // restoration, and the visual refresh ensures it looks consistent with
  // smart-seed when re-enabled.
  const renderSmartInterestsStep = () => (
    <div className="create-playlist-modal__smart">
      <SmartModalHero
        eyebrow={getText(staticText, 'smartHeroEyebrow', 'Alchemize a Playlist')}
        question={getText(staticText, 'smartInterestsQuestion', 'Anything else?')}
        hint={getText(staticText, 'smartInterestsHint', 'Include more interests (optional)')}
      />
      <SparkleDivider />
      {/* Seed-suggestion chips. Hardcoded for now — backend
          `divinationSeedQuestionList` query with `feature: ALCHEMIZE`
          isn't yet exposed. Tap a chip to fill the ChatInput value (does
          NOT auto-submit so the user can edit before generating). */}
      <div className="create-playlist-modal__seed-suggestions">
        <span className="create-playlist-modal__seed-suggestions-label">
          {getText(staticText, 'seedSuggestionsLabel', 'Try one of these')}
        </span>
        <div className="oracle-greeting-card__pill-row create-playlist-modal__seed-chips">
          {seedSuggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              className="oracle-greeting-card__pill"
              onClick={() => setInterestsText(suggestion)}
              aria-label={`${getText(staticText, 'useSeedAriaLabel', 'Use seed:')} ${suggestion}`}
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>
      <div className="create-playlist-modal__interest-row create-playlist-modal__seed-input">
        <ChatInput
          value={interestsText}
          onValueChange={setInterestsText}
          // Enter on the wizard's last step submits the Create flow via
          // the existing handler, matching the footer button's behavior.
          onSendMessage={(text) => {
            const trimmed = text.trim()
            setInterestsText(trimmed)
            handleSmartGenerate()
          }}
          placeholder={getText(staticText, 'smartInterestsPlaceholder', 'I want to learn to meditate…')}
          disabled={isGenerating}
        />
      </div>
      {renderSmartFooter(
        getText(staticText, 'create', 'Create'),
        handleSmartGenerate,
        isGenerating,
        false,
      )}
    </div>
  )

  const renderGeneratingStep = () => {
    // Phased loading copy keyed off `loadingPhase` (0/1/2). Cosmetic only.
    const phases: Array<{ key: string; fallback: string }> = [
      { key: 'phase1Loading', fallback: 'Understanding your request…' },
      { key: 'phase2Loading', fallback: 'Searching Gaia\'s library…' },
      { key: 'phase3Loading', fallback: 'Sequencing your playlist…' },
    ]
    const phase = phases[loadingPhase] ?? phases[0]
    return (
    <div className="create-playlist-modal__generating">
      <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
      <p className="create-playlist-modal__generating-text">
        {getText(staticText, phase.key, phase.fallback)}
      </p>
      {error ? (
        <div className="create-playlist-modal__generating-error">
          <span className="create-playlist-modal__error">{error}</span>
          <div className="create-playlist-modal__generating-error-actions">
            <Button type={BUTTON_TYPES.GHOST} onClick={handleChangePrefs}>
              {getText(staticText, 'changePreferences', 'Change preferences')}
            </Button>
            <Button type={BUTTON_TYPES.SECONDARY} onClick={handleReroll}>
              {getText(staticText, 'tryAgain', 'Try again')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
    )
  }

  const renderResultsStep = () => (
    <div className="create-playlist-modal__results">
      {/* Page-level heading lives inline with the step body (matches
          smart-seed).  `eyebrow=""` suppresses the in-step eyebrow because
          renderHeader already paints "ALCHEMIZED PLAYLIST" next to the
          close button.  The "Your New Playlist" string moves from the
          legacy <H3> in the modal header into the SmartModalHero
          question slot. */}
      <SmartModalHero
        eyebrow=""
        question={getText(staticText, 'titleResults', 'Your New Playlist')}
      />
      {/* The backend's curated 1-2 sentence narrative is the same string we
          ship as `description` on save (see handleSavePlaylist). Surfacing it
          here as read-only display copy gives the user a chance to "Try
          Again" if the framing doesn't fit.  Hidden when empty so we never
          render an empty paragraph. */}
      {narrativeText ? (
        <p className="create-playlist-modal__results-description">
          {narrativeText}
        </p>
      ) : null}
      {/* Final playlist renders in a CSS Grid — 2 cols mobile / 4 cols
          desktop, presentation-only cards. See ResultsVideoGrid. */}
      <ResultsVideoGrid
        documents={documents}
        getDocumentThumbnail={getDocumentThumbnail}
      />

      <div className="create-playlist-modal__results-form">
        <div className="create-playlist-modal__results-input-group">
          <label className="create-playlist-modal__results-input-label" htmlFor="playlist-title-input">
            {getText(staticText, 'playlistTitleLabel', 'Playlist Title')}
          </label>
          <input
            id="playlist-title-input"
            type="text"
            className="create-playlist-modal__input create-playlist-modal__input--results"
            placeholder={getText(staticText, 'playlistNamePlaceholder', 'Playlist name')}
            value={playlistNameForSave}
            onChange={(e) => setPlaylistNameForSave(e.target.value)}
            maxLength={100}
            aria-label={getText(staticText, 'playlistNameAriaLabel', 'Playlist name')}
          />
        </div>

        {(error || generationError) ? (
          <span className="create-playlist-modal__error">{error || generationError}</span>
        ) : null}

        {/* Action row mirrors the smart-seed Back / Generate pair:
            bare `<button>` elements using the divination-feature-entry
            outlined / filled variants.  Same sizing rules apply
            (min-height 44 px, padding 10px 24px, flex-centered) via the
            `__results-btn` SCSS that now matches `__seed-back-btn` /
            `__seed-generate-btn`. */}
        <div className="create-playlist-modal__results-actions">
          <button
            type="button"
            className={[
              'divination-feature-entry__button',
              'divination-feature-entry__button--outlined',
              'create-playlist-modal__results-btn',
            ].join(' ')}
            onClick={handleReroll}
            disabled={isGenerating}
          >
            {getText(staticText, 'tryAgain', 'Try Again')}
          </button>
          <button
            type="button"
            className={[
              'divination-feature-entry__button',
              'divination-feature-entry__button--filled',
              'create-playlist-modal__results-btn',
            ].join(' ')}
            onClick={handleSavePlaylist}
            disabled={isSavingPlaylist || !playlistNameForSave.trim()}
          >
            {isSavingPlaylist
              ? getText(staticText, 'saving', 'Saving…')
              : getText(staticText, 'savePlaylist', 'Save Playlist')}
          </button>
        </div>
      </div>
    </div>
  )

  // The seed + interests screens now render their own white surface via
  // <FloatingCard className="oracle-greeting-card">, so the modal frame
  // stays the project's default white background.  We only keep the
  // existing `--smart` width modifier and the `--results` variant.
  // Modifier classes on the modal frame.  smart-seed gets an extra
  // `--smart-seed` modifier on TOP of `--smart` so its tighter top padding
  // can be scoped without leaking onto the mood/time/interests pages.
  const modalModifier = (() => {
    if (step === 'results') return ' create-playlist-modal--results'
    if (step === 'generating') {
      if (previousStep.current === 'results') return ' create-playlist-modal--results'
      if (previousStep.current === 'smart-seed') {
        return ' create-playlist-modal--smart create-playlist-modal--smart-seed'
      }
      return ' create-playlist-modal--smart'
    }
    if (step === 'smart-seed') {
      return ' create-playlist-modal--smart create-playlist-modal--smart-seed'
    }
    if (isSmartStep) return ' create-playlist-modal--smart'
    return ''
  })()

  // Renderers keyed by step. `Record<Step, …>` makes TypeScript enforce
  // exhaustiveness: adding a new step or removing one fails the build
  // until the map is in sync. The generating step ghosts whichever step
  // was active before so the modal keeps its current dimensions during
  // the transition.
  const renderGeneratingWithGhost = () => {
    const ghostRenderer = (() => {
      if (previousStep.current === 'results') return renderResultsStep
      if (previousStep.current === 'smart-seed') return renderSmartSeedStep
      if (previousStep.current === 'smart-mood') return renderSmartMoodStep
      if (previousStep.current === 'smart-time') return renderSmartTimeStep
      return renderSmartInterestsStep
    })()
    return (
      <div className="create-playlist-modal__generating-wrapper">
        <div className="create-playlist-modal__generating-ghost">
          {ghostRenderer()}
        </div>
        <div className="create-playlist-modal__generating-overlay">
          {renderGeneratingStep()}
        </div>
      </div>
    )
  }

  const STEP_RENDERERS: Record<Step, () => React.ReactNode> = {
    choose: renderChooseStep,
    empty: renderEmptyStep,
    'smart-seed': renderSmartSeedStep,
    'smart-mood': renderSmartMoodStep,
    'smart-time': renderSmartTimeStep,
    'smart-interests': renderSmartInterestsStep,
    generating: renderGeneratingWithGhost,
    results: renderResultsStep,
  }

  const modalContent = (
    <div
      className="create-playlist-modal__overlay"
      onClick={onClose}
      role="presentation"
    >
      <div
        className={`create-playlist-modal${modalModifier}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        {renderHeader()}
        {STEP_RENDERERS[step]()}
      </div>
    </div>
  )

  // Portal to document.body to escape CSS contain:paint on ancestor elements,
  // which otherwise constrains position:fixed to the container instead of the viewport.
  if (typeof document !== 'undefined') {
    return createPortal(modalContent, document.body)
  }
  return modalContent
}

export default connectStaticText({ storeKey: 'createPlaylistModal' })(CreatePlaylistModal)
